import { createStore } from "vuex";

// Helper function to load cart from localStorage
const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem("cartItems");
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Error loading cart from localStorage:", error);
    return [];
  }
};

// Helper function to save cart to localStorage
const saveCartToStorage = (cartItems) => {
  try {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  } catch (error) {
    console.error("Error saving cart to localStorage:", error);
  }
};

export default createStore({
  state: {
    user: null,
    formData: {
      topic: "",
      grade_term_id: "",
      subject_term_id: "",
      domain_term_id: "",
      software_term_id: "",
      student_profile_term_ids: [],
    },
    result1Loading: false,
    result2Loading: false,
    result3Loading: false,
    selectedLessonPackage: null,
    // Flag for auto-processing cart after login
    shouldAutoProcessCart: false,
    // Cart state
    cart: {
      items: loadCartFromStorage(),
      isSubmitting: false,
      lastUpdated: Date.now(),
    },
  },
  mutations: {
    SET_USER_DATA(state, userData) {
      state.user = userData;
    },
    SET_FORM_DATA(state, formData) {
      state.formData = formData;
    },
    SET_FORM_RESULTS(state, formResult) {
      state.formResult = formResult;
    },
    SET_RESULTS1_LOADING(state, value) {
      state.result1Loading = value;
    },
    SET_RESULTS2_LOADING(state, value) {
      state.result2Loading = value;
    },
    SET_RESULTS3_LOADING(state, value) {
      state.result3Loading = value;
    },
    SET_SELECTED_LESSON_PACKAGE(state, value) {
      state.selectedLessonPackage = value;
    },

    // Cart auto-process mutations
    SET_AUTO_PROCESS_CART(state, shouldProcess) {
      state.shouldAutoProcessCart = shouldProcess;
    },

    // Cart mutations
    ADD_TO_CART(state, item) {
      // Check if item already exists
      const existingIndex = state.cart.items.findIndex(
        (cartItem) => cartItem.makercart_id === item.makercart_id
      );

      if (existingIndex === -1) {
        state.cart.items.push({
          id: item.makercart_id,
          makercart_id: item.makercart_id,
          title: item.makercart_activity,
          image:
            item.image_thumbnail && item.image_thumbnail.length > 0
              ? item.image_thumbnail[0]
              : "",
          description: item.activity_description,
          numberOfStudents: item.numberOfStudents,
          subject: item.subject,
          grade: item.grade,
          focus: item.focus,
        });
        state.cart.lastUpdated = Date.now();
        saveCartToStorage(state.cart.items);
      }
    },

    REMOVE_FROM_CART(state, index) {
      if (index >= 0 && index < state.cart.items.length) {
        state.cart.items.splice(index, 1);
        state.cart.lastUpdated = Date.now();
        saveCartToStorage(state.cart.items);
      }
    },

    CLEAR_CART(state) {
      state.cart.items = [];
      state.cart.lastUpdated = Date.now();
      localStorage.removeItem("cartItems");
    },

    SET_CART_ITEMS(state, items) {
      state.cart.items = items || [];
      state.cart.lastUpdated = Date.now();
      saveCartToStorage(state.cart.items);
    },

    SET_CART_SUBMITTING(state, isSubmitting) {
      state.cart.isSubmitting = isSubmitting;
    },

    // Initialize cart from localStorage (useful for app startup)
    INIT_CART_FROM_STORAGE(state) {
      state.cart.items = loadCartFromStorage();
      state.cart.lastUpdated = Date.now();
    },

    // Update a specific cart item (useful for image updates, etc.)
    UPDATE_CART_ITEM(state, { index, updates }) {
      if (index >= 0 && index < state.cart.items.length) {
        Object.assign(state.cart.items[index], updates);
        state.cart.lastUpdated = Date.now();
        saveCartToStorage(state.cart.items);
      }
    },
  },
  actions: {
    updateUserData({ commit }, userData) {
      commit("SET_USER_DATA", userData);
    },

    // Cart actions
    addToCart({ commit, getters }, { item, grade, focus, numberOfStudents }) {
      // Check if item already exists
      const existingItem = getters.getCartItemById(item.makercart_id);
      if (existingItem) {
        throw new Error("The item is already in the cart");
      }

      // Validate required fields
      if (!grade || !focus) {
        throw new Error(
          "Please select both grade and focus before adding to cart."
        );
      }

      const cartItem = {
        ...item,
        grade,
        focus,
        numberOfStudents: numberOfStudents || 80,
      };

      commit("ADD_TO_CART", cartItem);
    },

    removeFromCart({ commit }, index) {
      commit("REMOVE_FROM_CART", index);
    },

    clearCart({ commit }) {
      commit("CLEAR_CART");
    },

    initializeCart({ commit }) {
      commit("INIT_CART_FROM_STORAGE");
    },

    async submitCart({ commit, state }, submitFunction) {
      if (state.cart.items.length === 0) {
        throw new Error("Cart is empty");
      }

      commit("SET_CART_SUBMITTING", true);

      try {
        const result = await submitFunction(state.cart.items);
        if (result.success) {
          commit("CLEAR_CART");
        }
        return result;
      } finally {
        commit("SET_CART_SUBMITTING", false);
      }
    },

    updateCartItem({ commit }, { index, updates }) {
      commit("UPDATE_CART_ITEM", { index, updates });
    },

    // Auto-process cart actions
    setAutoProcessCart({ commit }, shouldProcess) {
      commit("SET_AUTO_PROCESS_CART", shouldProcess);
    },
  },
  getters: {
    getUserData: (state) => state.user,
    isAuthenticated: (state) => !!state.user,

    // Cart getters
    getCartItems: (state) => state.cart.items,
    getCartItemCount: (state) => state.cart.items.length,
    getCartItemById: (state) => (id) => {
      return state.cart.items.find((item) => item.makercart_id === id);
    },
    isCartEmpty: (state) => state.cart.items.length === 0,
    isCartSubmitting: (state) => state.cart.isSubmitting,
    getCartLastUpdated: (state) => state.cart.lastUpdated,

    // Validation getters
    canSubmitCart: (state) => {
      return state.cart.items.length >= 4 && !state.cart.isSubmitting;
    },
    getCartValidationMessage: (state) => {
      if (state.cart.items.length === 0) return "Cart is empty";
      if (state.cart.items.length < 4) return "Minimum 4 items required";
      if (state.cart.isSubmitting) return "Submitting cart...";
      return "";
    },

    // Auto-process getter
    getShouldAutoProcessCart: (state) => state.shouldAutoProcessCart,
  },
});
